// =============================================================================
// domain/reconcile.ts — which SOURCE recorded this session, and do they agree?
// -----------------------------------------------------------------------------
// M3. The replacement for the old client's duplicate-trade finder, and it is
// deliberately a different shape. **Do not port the row-matching design** (D143).
//
// ── 🚨 WHY ROW MATCHING CANNOT WORK, MEASURED ──
//
// The old client compares trade against trade. On Josh's 815-trade book that
// gave a **0% true-positive rate** — every group it ever offered was wrong, and
// accepting them all would have deleted $143 and 13 real contracts (D142).
//
// It is not a tuning problem. The real failure is not a row written twice: it is
// **one session recorded by two or three sources that disagree about how fills
// group into positions.** On 2026-08-05, account `bbc733b3` holds 65 rows for a
// single session from three sources, and the counterparts are 1-to-2, 1-to-3 and
// sometimes 1-to-0. A tool that matches rows cannot see any of it.
//
//   source                  rows   contracts   net
//   Sierra file               38         150   +$1,628.25
//   ATAS file                 19          55      −$85.50
//   realtime logger            8          52   −$1,673.00
//
// The app shows that day as −$130.25. The best reading of it is −$1,462.25.
//
// ── THE FIVE PROPERTIES D143 ASKED FOR ──
//
//   1. reconcile at POSITION level, not row level
//   2. compare SOURCES, and offer to drop one — the unit of the decision is an
//      import batch or a feed, never a row
//   3. say when a source is a strict SUBSET — the safe case, and what makes
//      "remove this one" a decision rather than a gamble
//   4. 🚨 NEVER PREFER A FILE OVER THE LIVE FEED. On that same day the ATAS
//      export was MISSING the largest loss of the session (MNQ long 10,
//      −$1,342.00). **An export is not a backup.**
//   5. flag a source whose DIRECTION MIX disagrees with another's — zero longs
//      where another source shows two would have caught Sierra's mis-pairing
//      immediately
//
// ⚠ THIS MODULE DECIDES NOTHING AND DELETES NOTHING. It produces a reading. The
// removal is an existing, batch-level "undo import", and it stays the user's —
// D143's third recommendation is a gap in the tooling (undoing Sierra's batch
// would also take the only MES record that exists), and a module that quietly
// resolved that would be making his call for him.
// =============================================================================

/** A trade, reduced to what reconciliation needs. */
export interface ReconcileTrade {
  id: string;
  /** Import batch id, or `LIVE_SOURCE` for the realtime feed / hand entry. */
  sourceKey: string;
  /** What to call it on screen — a filename, or "Realtime logger". */
  sourceLabel: string;
  account: string;
  date: string;
  contract: string;
  side: 'long' | 'short';
  qty: number;
  /** `HH:MM:SS`. */
  timeIn: string;
  netPnl: number;
}

/** The sentinel for trades with no import batch: the live feed, or typed in. */
export const LIVE_SOURCE = '__live__';

/**
 * One position: every row of a source that entered the same instrument, on the
 * same side, at effectively the same moment.
 */
export interface Position {
  contract: string;
  side: 'long' | 'short';
  /** The earliest entry in the cluster. */
  timeIn: string;
  qty: number;
  netPnl: number;
  tradeIds: string[];
}

export interface SourceSummary {
  key: string;
  label: string;
  isLive: boolean;
  positions: Position[];
  trades: number;
  qty: number;
  netPnl: number;
  /** Positions by side, for the direction check. */
  longs: number;
  shorts: number;
}

export interface SourcePair {
  /** The source being judged. */
  key: string;
  /** The source it was judged against. */
  againstKey: string;
  /** Positions of `key` with an exact counterpart in `againstKey`. */
  matched: number;
  /** Positions of `key` with none. */
  unmatched: Position[];
  /**
   * Every position of `key` has a counterpart — so removing it loses nothing.
   *
   * ⚠ True redundancy, not similarity: same instrument, same side, same
   * contracts and the same net to the cent, within the time tolerance.
   */
  isSubset: boolean;
}

export interface DirectionConflict {
  contract: string;
  /** The source showing none of a direction. */
  key: string;
  againstKey: string;
  side: 'long' | 'short';
  /** How many positions of that side the other source has. */
  otherCount: number;
}

/** A source judged against everything else that recorded the same day. */
export interface Redundancy {
  key: string;
  /** Positions with a counterpart in SOME other source. */
  matched: number;
  /** Positions nothing else has — what would be lost by removing this source. */
  unique: Position[];
  /** Every position exists elsewhere. Removing it loses nothing. */
  isRedundant: boolean;
}

export interface ReconcileReport {
  account: string;
  date: string;
  sources: SourceSummary[];
  pairs: SourcePair[];
  /**
   * 🚨 THE ONE THAT ACTUALLY DECIDES ANYTHING, and it is NOT the pairwise view.
   *
   * On 2026-08-05 the ATAS file was provably removable — every one of its 19
   * rows exists elsewhere — but it is a subset of **neither other source on its
   * own**: its six MNQ positions are duplicated by the live feed and its three
   * MES trades by Sierra. Judged pairwise it looks unique against both, and the
   * one unambiguously safe action available would never have been offered.
   *
   * The pairwise view above is kept because it is what a human reads to
   * understand WHY. This is what a recommendation may be built on.
   */
  redundancy: Redundancy[];
  directionConflicts: DirectionConflict[];
  /**
   * Sources whose every position is duplicated elsewhere, and which are NOT the
   * live feed. The only removal this module will put its name to.
   */
  safeToRemove: string[];
  /** Net if nothing is touched — what the app shows today. */
  netAsRecorded: number;
}

export interface ReconcileOptions {
  /**
   * How far apart two entries may be and still be the same position, in seconds.
   *
   * ⚠ Five, and both directions of error are real. Sierra and ATAS recorded the
   * same three MES trades **one second apart** on the clock, so exact matching
   * misses genuine counterparts; but Josh scalps, and a wide window would merge
   * two distinct entries into one position and then declare a source redundant
   * that is not. Five seconds covers clock skew between two recorders of the
   * same fill without reaching a re-entry.
   */
  toleranceSecs?: number;
}

const DEFAULT_TOLERANCE = 5;

/** `HH:MM:SS` → seconds. `NaN`-safe: an unreadable time sorts first and matches nothing. */
function secs(t: string): number {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(String(t || '').trim());
  if (!m) return Number.NaN;
  return +(m[1] as string) * 3600 + +(m[2] as string) * 60 + +(m[3] ?? '0');
}

const money = (n: number): number => Math.round(n * 100) / 100;

/**
 * Cluster one source's rows into positions.
 *
 * ⚠ Clustered against the FIRST row of the cluster, not the previous one. Chain
 * from the previous and a run of rows one second apart drifts arbitrarily far —
 * ten partial fills would swallow a genuine re-entry forty seconds later.
 */
export function groupIntoPositions(
  trades: readonly ReconcileTrade[],
  toleranceSecs = DEFAULT_TOLERANCE,
): Position[] {
  const byKey = new Map<string, ReconcileTrade[]>();
  for (const t of trades) {
    const k = `${t.contract}|${t.side}`;
    const list = byKey.get(k);
    if (list) list.push(t); else byKey.set(k, [t]);
  }

  const out: Position[] = [];
  for (const list of byKey.values()) {
    const sorted = [...list].sort((a, b) => (secs(a.timeIn) || 0) - (secs(b.timeIn) || 0));
    let cur: Position | null = null;
    let anchor = 0;
    for (const t of sorted) {
      const s = secs(t.timeIn);
      if (!cur || Number.isNaN(s) || Number.isNaN(anchor) || s - anchor > toleranceSecs) {
        cur = {
          contract: t.contract, side: t.side, timeIn: t.timeIn,
          qty: 0, netPnl: 0, tradeIds: [],
        };
        anchor = s;
        out.push(cur);
      }
      cur.qty += t.qty;
      cur.netPnl = money(cur.netPnl + t.netPnl);
      cur.tradeIds.push(t.id);
    }
  }
  return out.sort((a, b) => (secs(a.timeIn) || 0) - (secs(b.timeIn) || 0));
}

/**
 * Does `p` have an exact counterpart among `others`?
 *
 * 🚨 EXACT, NOT SIMILAR — same instrument, same side, same contracts, and the
 * same net to the cent. The whole value of a subset finding is that removing the
 * source is provably lossless, and a fuzzy match would turn that promise into a
 * guess. Two positions that are merely close are reported as unmatched, which is
 * the honest answer: the user then looks at both.
 */
function counterpart(p: Position, others: readonly Position[], tol: number, used: Set<number>): number {
  for (let i = 0; i < others.length; i++) {
    if (used.has(i)) continue;
    const o = others[i] as Position;
    if (o.contract !== p.contract || o.side !== p.side) continue;
    if (o.qty !== p.qty) continue;
    if (Math.abs(o.netPnl - p.netPnl) > 0.01) continue;
    const dt = Math.abs(secs(o.timeIn) - secs(p.timeIn));
    if (!Number.isFinite(dt) || dt > tol) continue;
    return i;
  }
  return -1;
}

/**
 * Reconcile every source that recorded one account-day.
 *
 * ⚠ ONE ACCOUNT, ONE DAY. Two accounts trading the same instrument at the same
 * moment is ordinary copy-trading, not duplication (D144 is the whole lesson),
 * and across days nothing can match by construction.
 */
export function reconcileAccountDay(
  trades: readonly ReconcileTrade[],
  opts: ReconcileOptions = {},
): ReconcileReport {
  const tol = opts.toleranceSecs ?? DEFAULT_TOLERANCE;
  const account = trades[0]?.account ?? '';
  const date = trades[0]?.date ?? '';

  const bySource = new Map<string, ReconcileTrade[]>();
  for (const t of trades) {
    const list = bySource.get(t.sourceKey);
    if (list) list.push(t); else bySource.set(t.sourceKey, [t]);
  }

  const sources: SourceSummary[] = [];
  for (const [key, list] of bySource) {
    const positions = groupIntoPositions(list, tol);
    sources.push({
      key,
      label: list[0]?.sourceLabel ?? key,
      isLive: key === LIVE_SOURCE,
      positions,
      trades: list.length,
      qty: list.reduce((n, t) => n + t.qty, 0),
      netPnl: money(list.reduce((n, t) => n + t.netPnl, 0)),
      longs: positions.filter((p) => p.side === 'long').length,
      shorts: positions.filter((p) => p.side === 'short').length,
    });
  }

  const pairs: SourcePair[] = [];
  const directionConflicts: DirectionConflict[] = [];

  for (const a of sources) {
    for (const b of sources) {
      if (a.key === b.key) continue;

      // ⚠ `used` makes the match one-to-one. Without it, three ATAS partials
      // that each equal one live position would all claim the same counterpart
      // and a source would look redundant on the strength of one row.
      const used = new Set<number>();
      const unmatched: Position[] = [];
      let matched = 0;
      for (const p of a.positions) {
        const i = counterpart(p, b.positions, tol, used);
        if (i >= 0) { used.add(i); matched++; } else unmatched.push(p);
      }
      pairs.push({
        key: a.key, againstKey: b.key, matched, unmatched,
        isSubset: a.positions.length > 0 && unmatched.length === 0,
      });

      // ── 5. DIRECTION MIX ────────────────────────────────────────────────
      //
      // 🚨 Zero longs on an instrument another source shows longs on is not a
      // difference of opinion — one of them paired the fills wrongly. On
      // 2026-08-05 Sierra recorded 42 MNQ short lots and ZERO longs, on a day
      // the live feed shows two longs, and booked MNQ at +$1,417.50 where the
      // position-aware record says −$1,673.00. It had taken the sell that
      // CLOSED a long and treated it as opening a short.
      //
      // ⚠ Only where BOTH sources traded the instrument. A source that never
      // touched MNQ is not in conflict about MNQ.
      const contracts = new Set(a.positions.map((p) => p.contract));
      for (const c of contracts) {
        if (!b.positions.some((p) => p.contract === c)) continue;
        for (const side of ['long', 'short'] as const) {
          const mine = a.positions.filter((p) => p.contract === c && p.side === side).length;
          const theirs = b.positions.filter((p) => p.contract === c && p.side === side).length;
          if (mine === 0 && theirs > 0) {
            directionConflicts.push({ contract: c, key: a.key, againstKey: b.key, side, otherCount: theirs });
          }
        }
      }
    }
  }

  // ── 3. REDUNDANT AGAINST THE UNION OF THE OTHERS, NOT AGAINST ANY ONE ─────
  //
  // 🚨 THE PAIRWISE VIEW WOULD HAVE MISSED THE ONE SAFE ACTION ON 2026-08-05.
  // The ATAS file is provably removable — all 19 rows exist elsewhere — but it
  // is a subset of neither other source alone: its six MNQ positions are
  // duplicated by the LIVE feed and its three MES trades by SIERRA. Judged
  // against either one it looks unique, and the only unambiguous action
  // available would never have been offered.
  const redundancy: Redundancy[] = sources.map((s) => {
    // The pool is every OTHER source's positions, matched one-to-one so two
    // copies of a position cannot both be covered by a single counterpart.
    const pool: Position[] = [];
    for (const o of sources) if (o.key !== s.key) pool.push(...o.positions);
    const used = new Set<number>();
    const unique: Position[] = [];
    let matched = 0;
    for (const p of s.positions) {
      const i = counterpart(p, pool, tol, used);
      if (i >= 0) { used.add(i); matched++; } else unique.push(p);
    }
    return { key: s.key, matched, unique, isRedundant: s.positions.length > 0 && unique.length === 0 };
  });

  // ── 4. THE ONLY REMOVAL THIS MODULE WILL NAME ─────────────────────────────
  //
  // 🚨 Redundant AND not the live feed, however redundant the live feed looks.
  // D143's finding 2 is the counter-example that settles it: on that very day
  // the ATAS export was MISSING the largest loss of the session — MNQ long 10,
  // −$1,342.00 — so a rule willing to prefer a file over the feed would have
  // silently dropped it. An export is not a backup.
  const safeToRemove = redundancy
    .filter((r) => r.isRedundant && !sources.find((s) => s.key === r.key)?.isLive)
    .map((r) => r.key);

  return {
    account, date, sources, pairs, redundancy, directionConflicts, safeToRemove,
    netAsRecorded: money(trades.reduce((n, t) => n + t.netPnl, 0)),
  };
}

/**
 * Every account-day recorded by more than one source.
 *
 * ⚠ Single-source days are dropped before any work is done. On a real book they
 * are the overwhelming majority, and reconciliation has nothing to say about a
 * day only one recorder saw.
 */
export function findMultiSourceDays(
  trades: readonly ReconcileTrade[],
  opts: ReconcileOptions = {},
): ReconcileReport[] {
  const byDay = new Map<string, ReconcileTrade[]>();
  for (const t of trades) {
    const k = `${t.account}|${t.date}`;
    const list = byDay.get(k);
    if (list) list.push(t); else byDay.set(k, [t]);
  }

  const out: ReconcileReport[] = [];
  for (const list of byDay.values()) {
    if (new Set(list.map((t) => t.sourceKey)).size < 2) continue;
    out.push(reconcileAccountDay(list, opts));
  }
  return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}
